import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// API Configuration
const API_CONFIG = {
  BASE_URL: 'https://fftestservice.sarsetech.com/',
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
};

// API Endpoints
const ENDPOINTS = {
  REVIEW_LIST: 'user/listReviewsProducts',
  REVIEW_ADD: 'user/addReview',
  REVIEW_UPDATE: 'user/updateReviewStatus',
  REVIEW_DELETE: 'user/deleteReview',
  REVIEW_COUNT: 'user/reviewCount',
  REVIEW_DETAIL: 'admin/reviewDetail',
};

// Types
export interface Review {
  id: number;              // This is the actual review ID from database
  review_id?: number;      // Alias (optional)
  rating: number;
  comment: string;
  created_date: string;
  customer_name: string;
  product_id: number;
  seller_id: number | null;
  status: number;          // 0-pending, 1-approved, 2-rejected
  product_name?: string;
  user_id?: number;
  review_type?: string;
  type?: number;           // 0-product, 1-seller
  seller_name?: string;
}

export interface ApiResponse<T> {
  error: boolean;
  message: string;
  data?: T;
  result?: T;
  count?: number;
}

export interface ReviewListParams {
  page?: number;
  limit?: number;
  search_str?: string;
  status?: number;
  product_id?: number;
  seller_id?: number;
  type?: number;
}

export interface ReviewCountParams {
  search_str?: string;
  status?: number;
  product_id?: number;
  seller_id?: number;
  type?: number;
}

export interface ReviewDetailParams {
  review_id: number;
}

export interface ReviewDeleteParams {
  review_id: number;
}

export interface AddReviewRequest {
  product_id: number;
  seller_id: number | null;
  rating: number;
  comment: string;
  type?: number;
}

export interface UpdateReviewRequest {
  review_id: number;
  status: number;
}

export interface ReviewFormData {
  product_id: number;
  seller_id: number | null;
  rating: number;
  comment: string;
  status: number;
  type?: number;
}

export interface FormErrors {
  [key: string]: string;
}

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

// Create the API
export const reviewApi = createApi({
  reducerPath: 'reviewApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      headers.set('authkey', API_CONFIG.AUTH_KEY);
      headers.set('Content-Type', 'application/json');
      
      return headers;
    },
  }),
  tagTypes: ['Review', 'ReviewCount', 'ReviewList', 'PendingCount'],
  endpoints: (build) => ({
    // 1. List Reviews
    listReviews: build.query<ApiResponse<Review[]>, ReviewListParams>({
      query: (params = {}) => {
        const { page = 1, limit = 10, search_str = '', status, product_id, seller_id, type } = params;
        
        const body: any = {
          start: (page - 1) * limit,
          limit,
        };

        if (search_str?.trim()) {
          body.search_str = search_str.trim();
        }
        
        if (status !== undefined) {
          body.status = status;
        }
        
        if (product_id !== undefined && product_id > 0) {
          body.product_id = product_id;
        }
        
        if (seller_id !== undefined && seller_id > 0) {
          body.seller_id = seller_id;
        }
        
        if (type !== undefined) {
          body.type = type;
        }

        console.log('List Reviews Request Body:', body);

        return {
          url: ENDPOINTS.REVIEW_LIST,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      providesTags: (result) => {
        const tags: any[] = ['ReviewList'];
        
        if (result?.result) {
          result.result.forEach((review) => {
            tags.push({ type: 'Review', id: review.id });
          });
        }
        
        return tags;
      },
      transformResponse: (response: any) => {
        console.log('List Reviews Raw Response:', response);
        
        // Backend returns data with id field (from database)
        const reviews = (response.result || response.data || []).map((review: any) => ({
          ...review,
          // Ensure id is always set correctly
          id: review.id || review.review_id || 0,
          // Keep both for compatibility
          review_id: review.id || review.review_id || 0
        }));
        
        console.log('Transformed Reviews:', reviews);
        
        return {
          error: response.error || false,
          message: response.message || '',
          result: reviews,
          count: response.count || response.total || 0,
        };
      },
    }),

    // 2. Add Review
    addReview: build.mutation<ApiResponse<any>, AddReviewRequest>({
      query: (data) => {
        console.log('Add Review Request:', data);
        return { 
          url: ENDPOINTS.REVIEW_ADD,
          method: 'POST',
          body: JSON.stringify(data),
        };
      },
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList', 'PendingCount'],
    }),

    // 3. Update Review Status
    updateReview: build.mutation<ApiResponse<any>, UpdateReviewRequest>({
      query: ({ review_id, status }) => {
        console.log('Update Review Request:', { review_id, status });
        return {
          url: ENDPOINTS.REVIEW_UPDATE,
          method: 'POST',
          body: JSON.stringify({ 
            review_id,
            status 
          }),
        };
      },
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList', 'PendingCount'],
    }),

    // 4. Delete Review
    deleteReview: build.mutation<ApiResponse<any>, ReviewDeleteParams>({
      query: ({ review_id }) => {
        console.log('Delete Review Request:', { review_id });
        return {
          url: ENDPOINTS.REVIEW_DELETE,
          method: 'POST',
          body: JSON.stringify({ review_id }),
        };
      },
      invalidatesTags: ['Review', 'ReviewCount', 'ReviewList', 'PendingCount'],
    }),

    // 5. Get Review Count
    getReviewCount: build.query<ApiResponse<{ count: number }>, ReviewCountParams>({
      query: (params = {}) => {
        const { search_str = '', status, product_id, seller_id, type } = params;
        
        const body: any = {};

        if (search_str?.trim()) {
          body.search_str = search_str.trim();
        }
        
        if (status !== undefined) {
          body.status = status;
        }
        
        if (product_id !== undefined && product_id > 0) {
          body.product_id = product_id;
        }
        
        if (seller_id !== undefined && seller_id > 0) {
          body.seller_id = seller_id;
        }
        
        if (type !== undefined) {
          body.type = type;
        }

        console.log('Review Count Request Body:', body);

        return {
          url: ENDPOINTS.REVIEW_COUNT,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      providesTags: ['ReviewCount'],
      transformResponse: (response: any) => {
        console.log('Review Count Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          count: response.count || response.result?.count || response.data?.count || 0,
        };
      },
    }),

    // 6. Get Pending Review Count
    getPendingReviewCount: build.query<ApiResponse<{ count: number }>, void>({
      query: () => ({
        url: ENDPOINTS.REVIEW_COUNT,
        method: 'POST',
        body: JSON.stringify({ status: 0 }),
      }),
      providesTags: ['PendingCount'],
      transformResponse: (response: any) => {
        console.log('Pending Review Count Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          count: response.count || response.result?.count || response.data?.count || 0,
        };
      },
    }),

    // 7. Get Rejected Review Count
    getRejectedReviewCount: build.query<ApiResponse<{ count: number }>, void>({
      query: () => ({
        url: ENDPOINTS.REVIEW_COUNT,
        method: 'POST',
        body: JSON.stringify({ status: 2 }),
      }),
      providesTags: ['PendingCount'],
      transformResponse: (response: any) => {
        console.log('Rejected Review Count Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          count: response.count || response.result?.count || response.data?.count || 0,
        };
      },
    }),

    // 8. Get Review Details
    getReviewDetail: build.query<ApiResponse<Review>, ReviewDetailParams>({
      query: ({ review_id }) => ({
        url: ENDPOINTS.REVIEW_DETAIL,
        method: 'POST',
        body: JSON.stringify({ review_id }),
      }),
      providesTags: (result, error, { review_id }) => [
        { type: 'Review', id: review_id },
      ],
      transformResponse: (response: any) => {
        console.log('Review Details Response:', response);
        const review = response.result || response.data;
        if (review) {
          review.id = review.id || review.review_id || 0;
          review.review_id = review.id;
        }
        return {
          error: response.error || false,
          message: response.message || '',
          result: review,
        };
      },
    }),
  }),
});

export const {
  useListReviewsQuery,
  useAddReviewMutation,
  useUpdateReviewMutation,
  useDeleteReviewMutation,
  useGetReviewCountQuery,
  useGetPendingReviewCountQuery,
  useGetRejectedReviewCountQuery,
  useGetReviewDetailQuery,
} = reviewApi;